/* The library rail's own chrome, at the two widths the 02.08 KANON gallery gives it.
 *
 * · 240px  `Tydzien z panelem dnia`'s third column, between the week and the 604px panel. The
 *          gallery's own note says why it sits there: "Biblioteka siedzi między nimi, bo jest
 *          źródłem przeciągania w prawo."
 * · 320px  the 07.09 programator's DRAWER, slid over the day panel by its host.
 *
 * ── The library itself is not here any more ──────────────────────────────────────────────────
 * The search field, the filter glyph and its panel, the active-selection chips, the count row, the
 * four states and the row are `entities/exercise-library`'s since 17 September 2026 —
 * `ExerciseLibraryList.module.css`. What is left is the box, the head's own eyebrow/title/✕, the
 * day-fit caption and the foot: everything about the DAY rather than about the catalogue.
 *
 * The rail has no padding of its own, and that is structural — the gallery builds it as stacked
 * blocks each carrying its own padding and its own hairline, so the hairlines run the full width
 * rather than stopping short of a gutter. */

.rail {
  display: flex;
  flex: none;
  flex-direction: column;
  width: 460px;
  min-height: 0;
  background: var(--bg-card);
  border-left: var(--border-hairline) solid var(--border);
}

/* The 320px DRAWER (`--w-lib-drawer`). The ONE library this app draws beside a day since
 * 19 September 2026, when the calendar's dock took it too and the 240px `compact` column left with
 * its last host. It fills the box its page gives it, and it draws no left hairline: in the builder
 * that edge IS the day panel's own, and on the calendar the dock's `.railHost` draws it. */
.drawer {
  /* The hover peek's containing block. It is the DRAWER's box the mock measures `left: calc(100% +
   * 10px)` and `top: 50%` against — 320px across and the panel's full height — so the card lands ten
   * pixels past the drawer's right edge, over the panel, and centred on it. Declared here rather
   * than on `.rail` so the two columns that share this component gain no positioned ancestor they
   * have no use for. */
  position: relative;
  width: 100%;
  border-left: 0;
}

/* ── The head's own row: the eyebrow, the title and the ✕ ─────────────────────────────────────
 * They stand INSIDE the library's head block, which the entity draws and pads — these are what the
 * rail hands it as its `heading`. */

.titleRow {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
}

.title {
  font-size: var(--fs-exercise);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-plan);
  color: var(--text);
}

.eyebrow {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-lab);
  text-transform: uppercase;
  color: var(--text-2);
}

/* The title takes the row and the ✕ sits at its end, so the two never depend on the title's
 * length — a session named "Push A" and one named "Trening obwodowy górnej części" put the ✕ in
 * the same place. */
.drawer .titleRow {
  align-items: center;
}

.drawer .title {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-label);
  letter-spacing: var(--ls-heading);
}

.closeButton {
  display: grid;
  flex: none;
  place-items: center;
  width: 26px;
  height: 26px;
  font-size: var(--fs-label);
  line-height: var(--lh-none);
  color: var(--text-2);
  cursor: pointer;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--r-circle);
}

.closeButton:hover {
  color: var(--text);
}

.closeButton:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* "Pasują do dnia Push A", and the press that lifts the narrowing it describes.
 *
 * The mock sets this in `--text-3`; it ships in `--text-2` because the standing rule against
 * `--text-3` as body ink is about legibility on the sunken ground, and this line is a control, which
 * makes it load-bearing rather than decorative.
 *
 * The action word carries an underline as well as its ink: a sentence that turns out to be a button
 * has to look like one before it is hovered, and colour alone is not a channel. */
.dayFit {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  align-items: baseline;
  padding: var(--space-0);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-medium);
  color: var(--text-2);
  text-align: left;
  background: transparent;
  border: 0;
}

.dayFit:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.dayFitAction {
  font-weight: var(--fw-bold);
  color: var(--text);
  text-decoration: underline;
}

.dayFit:hover .dayFitAction {
  text-decoration: none;
}

/* ── The foot ────────────────────────────────────────────────────────────────────────────── */

/* The foot — the mock's own row: what this library does, then `Wyczyść`, then the primary. The
 * sentence takes the width and the two controls keep theirs, so a long hint cannot push the button
 * off the drawer's right edge. The border and the padding moved here from `.hint` on 16 September
 * 2026, when the foot stopped being one paragraph. */
.foot {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-9px) var(--space-14px);
  border-top: var(--border-hairline) solid var(--border);
}

.drawer .foot {
  padding: var(--space-2) var(--space-11px);
}

.hint {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

.drawer .hint {
  font-size: var(--fs-label-sm);
  line-height: var(--lh-hint-dense);
}

.hint b {
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

/* `Wyczyść` — a bare text press, the mock's `multiClear`. It reuses nothing from the library's own
 * chip clear on purpose: that one is a filter chip's clear at the top of the rail, inked `--text-2`
 * in the eyebrow size, and this one is the quieter of two controls standing beside a filled
 * primary. */
.clearPicks {
  flex: none;
  padding: var(--space-0) var(--space-2);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  color: var(--text-3);
  cursor: pointer;
  background: transparent;
  border: 0;
}

.clearPicks:hover {
  color: var(--text);
}

.clearPicks:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The GEOMETRY of the primary; `shared/ui/inkButton` carries the treatment, and nothing is
 * declared on both sides — a declaration living in both is settled by chunk order, which differs
 * between the dev server and the build. The mock's own 32 x radius 10, at its 12px/600.
 *
 * Ink rather than ember: the drawer already spends its one accent on the filter dot and the day-fit
 * caption, and the mock draws this button as a plain light fill. */
.addPicks {
  height: var(--h-control);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  border-radius: var(--radius-sm);
}
